import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, RefreshCw, Edit3, Trash2 } from "lucide-react";

const emptyStory = { title: "", scam_type: "", amount_lost: 0, summary: "", lesson: "", state: "", date: "" };
const emptyScammer = { handle: "", telegram_username: "", upi_id: "", phone: "", company_name: "", website: "", status_tag: "high_risk", victim_count: 0, total_loss_inr: 0, summary: "" };

const SiteContentTab = () => {
  const [stories, setStories] = useState([]);
  const [scammers, setScammers] = useState([]);
  const [mentors, setMentors] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storyOpen, setStoryOpen] = useState(null);
  const [scammerOpen, setScammerOpen] = useState(null);
  const [mentorOpen, setMentorOpen] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, sc, m, c] = await Promise.all([
        api.get("/admin/stories"),
        api.get("/admin/scammers"),
        api.get("/admin/mentors"),
        api.get("/admin/contact-messages"),
      ]);
      setStories(s.data || []);
      setScammers(sc.data || []);
      setMentors(m.data || []);
      setContacts(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const saveStory = async () => {
    if (!storyOpen) return;
    try {
      if (storyOpen.mode === "new") await api.post("/admin/stories", storyOpen.item);
      else await api.patch(`/admin/stories/${storyOpen.item.id}`, storyOpen.item);
      toast.success("Story saved");
      setStoryOpen(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const saveScammer = async () => {
    if (!scammerOpen) return;
    try {
      if (scammerOpen.mode === "new") await api.post("/admin/scammers", scammerOpen.item);
      else await api.patch(`/admin/scammers/${scammerOpen.item.id}`, scammerOpen.item);
      toast.success("Scammer entry saved");
      setScammerOpen(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const saveMentor = async () => {
    if (!mentorOpen) return;
    try {
      await api.patch(`/admin/mentors/${mentorOpen.item.id}`, mentorOpen.item);
      toast.success("Mentor updated");
      setMentorOpen(null);
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const fieldCls = "bg-white/5 border-white/15 text-white";

  return (
    <div data-testid="admin-site-content">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-display text-lg font-bold text-white">Site content</h3>
        <Button onClick={fetchAll} variant="outline" className="border-white/15 text-white hover:bg-white/5 gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <Tabs defaultValue="stories">
        <TabsList className="bg-white/5 border border-white/10 mb-4">
          <TabsTrigger value="stories">Stories</TabsTrigger>
          <TabsTrigger value="scammers">Scammer DB</TabsTrigger>
          <TabsTrigger value="mentors">Mentors</TabsTrigger>
          <TabsTrigger value="contact">Contact inbox</TabsTrigger>
        </TabsList>

        <TabsContent value="stories">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setStoryOpen({ mode: "new", item: { ...emptyStory } })} className="bg-[#D4AF37] text-black font-bold gap-2">
              <Plus className="h-4 w-4" /> New story
            </Button>
          </div>
          <ContentTable loading={loading} empty={stories.length === 0} emptyText="No stories." cols={["Title", "Type", "Amount", "Date", ""]}>
            {stories.map((s) => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="px-3 py-3 text-white/90 max-w-[240px] truncate">{s.title}</td>
                <td className="px-3 py-3 text-white/60 text-xs">{s.scam_type}</td>
                <td className="px-3 py-3 text-white/75">₹{s.amount_lost}</td>
                <td className="px-3 py-3 text-white/55 text-xs">{s.date}</td>
                <td className="px-3 py-3 text-right">
                  <RowActions
                    onEdit={() => setStoryOpen({ mode: "edit", item: { ...s } })}
                    onDelete={async () => {
                      if (!window.confirm("Delete this story?")) return;
                      await api.delete(`/admin/stories/${s.id}`);
                      toast.success("Deleted");
                      fetchAll();
                    }}
                  />
                </td>
              </tr>
            ))}
          </ContentTable>
        </TabsContent>

        <TabsContent value="scammers">
          <div className="flex justify-end mb-3">
            <Button onClick={() => setScammerOpen({ mode: "new", item: { ...emptyScammer } })} className="bg-[#D4AF37] text-black font-bold gap-2">
              <Plus className="h-4 w-4" /> New entry
            </Button>
          </div>
          <ContentTable loading={loading} empty={scammers.length === 0} emptyText="No scammer entries." cols={["Handle", "Status", "Victims", "Loss", ""]}>
            {scammers.map((s) => (
              <tr key={s.id} className="border-t border-white/5">
                <td className="px-3 py-3 text-white/90">{s.handle}</td>
                <td className="px-3 py-3"><Badge className="text-[9px]">{s.status_tag}</Badge></td>
                <td className="px-3 py-3 text-white/75">{s.victim_count}</td>
                <td className="px-3 py-3 text-white/75">₹{s.total_loss_inr}</td>
                <td className="px-3 py-3 text-right">
                  <RowActions
                    onEdit={() => setScammerOpen({ mode: "edit", item: { ...s } })}
                    onDelete={async () => {
                      if (!window.confirm("Delete this entry?")) return;
                      await api.delete(`/admin/scammers/${s.id}`);
                      toast.success("Deleted");
                      fetchAll();
                    }}
                  />
                </td>
              </tr>
            ))}
          </ContentTable>
        </TabsContent>

        <TabsContent value="mentors">
          <ContentTable loading={loading} empty={mentors.length === 0} emptyText="No mentors." cols={["Name", "Category", "Verified", "Flagged", ""]}>
            {mentors.map((m) => (
              <tr key={m.id} className="border-t border-white/5">
                <td className="px-3 py-3 text-white/90">{m.name}</td>
                <td className="px-3 py-3 text-white/60 text-xs">{m.category}</td>
                <td className="px-3 py-3">{m.is_verified ? "Yes" : "No"}</td>
                <td className="px-3 py-3">{m.is_flagged ? "Yes" : "No"}</td>
                <td className="px-3 py-3 text-right">
                  <RowActions
                    onEdit={() => setMentorOpen({ item: { ...m } })}
                    onDelete={async () => {
                      if (!window.confirm(`Delete mentor ${m.name}?`)) return;
                      await api.delete(`/admin/mentors/${m.id}`);
                      toast.success("Deleted");
                      fetchAll();
                    }}
                  />
                </td>
              </tr>
            ))}
          </ContentTable>
        </TabsContent>

        <TabsContent value="contact">
          <ContentTable loading={loading} empty={contacts.length === 0} emptyText="No contact messages." cols={["From", "Subject", "Type", "Date", ""]}>
            {contacts.map((c) => (
              <tr key={c.id} className="border-t border-white/5">
                <td className="px-3 py-3">
                  <div className="text-white/90">{c.name}</div>
                  <div className="text-xs text-white/50">{c.email}</div>
                </td>
                <td className="px-3 py-3 text-white/75 max-w-[200px] truncate">{c.subject}</td>
                <td className="px-3 py-3 text-white/55 text-xs">{c.type}</td>
                <td className="px-3 py-3 text-white/55 text-xs">{(c.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-3 text-right">
                  <Button size="sm" variant="outline" className="border-rose-500/30 text-rose-300" onClick={async () => {
                    if (!window.confirm("Delete message?")) return;
                    await api.delete(`/admin/contact-messages/${c.id}`);
                    toast.success("Deleted");
                    fetchAll();
                  }}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </ContentTable>
        </TabsContent>
      </Tabs>

      <Dialog open={!!storyOpen} onOpenChange={(v) => !v && setStoryOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{storyOpen?.mode === "new" ? "New story" : "Edit story"}</DialogTitle></DialogHeader>
          {storyOpen && (
            <div className="space-y-2">
              <Input value={storyOpen.item.title} onChange={(e) => setStoryOpen({ ...storyOpen, item: { ...storyOpen.item, title: e.target.value } })} placeholder="Title" className={fieldCls} />
              <Input value={storyOpen.item.scam_type} onChange={(e) => setStoryOpen({ ...storyOpen, item: { ...storyOpen.item, scam_type: e.target.value } })} placeholder="Scam type" className={fieldCls} />
              <Input type="number" value={storyOpen.item.amount_lost} onChange={(e) => setStoryOpen({ ...storyOpen, item: { ...storyOpen.item, amount_lost: Number(e.target.value) } })} placeholder="Amount lost" className={fieldCls} />
              <Textarea value={storyOpen.item.summary} onChange={(e) => setStoryOpen({ ...storyOpen, item: { ...storyOpen.item, summary: e.target.value } })} placeholder="Summary" rows={3} className={fieldCls} />
              <Textarea value={storyOpen.item.lesson} onChange={(e) => setStoryOpen({ ...storyOpen, item: { ...storyOpen.item, lesson: e.target.value } })} placeholder="Lesson" rows={2} className={fieldCls} />
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveStory} className="bg-[#D4AF37] text-black font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!scammerOpen} onOpenChange={(v) => !v && setScammerOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>{scammerOpen?.mode === "new" ? "New scammer entry" : "Edit entry"}</DialogTitle></DialogHeader>
          {scammerOpen && (
            <div className="space-y-2">
              <Input value={scammerOpen.item.handle} onChange={(e) => setScammerOpen({ ...scammerOpen, item: { ...scammerOpen.item, handle: e.target.value } })} placeholder="Handle" className={fieldCls} />
              <Input value={scammerOpen.item.telegram_username || ""} onChange={(e) => setScammerOpen({ ...scammerOpen, item: { ...scammerOpen.item, telegram_username: e.target.value } })} placeholder="Telegram" className={fieldCls} />
              <Input value={scammerOpen.item.upi_id || ""} onChange={(e) => setScammerOpen({ ...scammerOpen, item: { ...scammerOpen.item, upi_id: e.target.value } })} placeholder="UPI" className={fieldCls} />
              <Input value={scammerOpen.item.status_tag || ""} onChange={(e) => setScammerOpen({ ...scammerOpen, item: { ...scammerOpen.item, status_tag: e.target.value } })} placeholder="Status tag" className={fieldCls} />
              <Textarea value={scammerOpen.item.summary || ""} onChange={(e) => setScammerOpen({ ...scammerOpen, item: { ...scammerOpen.item, summary: e.target.value } })} placeholder="Summary" rows={4} className={fieldCls} />
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveScammer} className="bg-[#D4AF37] text-black font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mentorOpen} onOpenChange={(v) => !v && setMentorOpen(null)}>
        <DialogContent className="bg-[#0c0c0c] border-white/10 text-white max-w-lg">
          <DialogHeader><DialogTitle>Edit mentor</DialogTitle></DialogHeader>
          {mentorOpen && (
            <div className="space-y-2">
              <Input value={mentorOpen.item.name} onChange={(e) => setMentorOpen({ ...mentorOpen, item: { ...mentorOpen.item, name: e.target.value } })} placeholder="Name" className={fieldCls} />
              <Input value={mentorOpen.item.category || ""} onChange={(e) => setMentorOpen({ ...mentorOpen, item: { ...mentorOpen.item, category: e.target.value } })} placeholder="Category" className={fieldCls} />
              <Textarea value={mentorOpen.item.bio || ""} onChange={(e) => setMentorOpen({ ...mentorOpen, item: { ...mentorOpen.item, bio: e.target.value } })} placeholder="Bio" rows={4} className={fieldCls} />
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!mentorOpen.item.is_verified} onChange={(e) => setMentorOpen({ ...mentorOpen, item: { ...mentorOpen.item, is_verified: e.target.checked } })} /> Verified</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={!!mentorOpen.item.is_flagged} onChange={(e) => setMentorOpen({ ...mentorOpen, item: { ...mentorOpen.item, is_flagged: e.target.checked } })} /> Flagged</label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveMentor} className="bg-[#D4AF37] text-black font-bold">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ContentTable = ({ loading, empty, emptyText, cols, children }) => (
  <div className="rounded-xl border border-white/10 overflow-hidden">
    <table className="w-full text-sm">
      <thead className="bg-white/[0.04] text-left text-[10px] uppercase tracking-widest text-white/55 font-mono-alt">
        <tr>{cols.map((c) => <th key={c || "actions"} className={`px-3 py-2.5 ${c === "" ? "text-right" : ""}`}>{c}</th>)}</tr>
      </thead>
      <tbody>
        {loading && <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-white/45">Loading…</td></tr>}
        {!loading && empty && <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-white/45">{emptyText}</td></tr>}
        {children}
      </tbody>
    </table>
  </div>
);

const RowActions = ({ onEdit, onDelete }) => (
  <>
    <Button size="sm" variant="outline" onClick={onEdit} className="border-white/15 text-white hover:bg-white/5 mr-2">
      <Edit3 className="h-3.5 w-3.5" />
    </Button>
    <Button size="sm" variant="outline" onClick={onDelete} className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10">
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
  </>
);

export default SiteContentTab;
